'use client';

import { useContext } from 'react';
import { useTranslation } from 'next-i18next';
import { MapIcon } from '@heroicons/react/24/outline';
import { Button } from '@components/common';
import { HeaderSearchContext } from '@components/contexts/HeaderSearchContext';
import type { ButtonProps } from '@type/anchors-buttons';
import type { JobSearchQueryParams } from '@type/filters';

const googleAPIKey = process.env.GOOGLE_MAPS_CLIENT_API_KEY as string;

interface MapButtonProps {
  locale: Locale;
  queryParams?: Partial<JobSearchQueryParams>;
  className?: string;
  iconClassName?: string;
  size?: ButtonProps['size'];
  rounding?: ButtonProps['rounding'];
  styling?: ButtonProps['styling'];
  color?: ButtonProps['color'];
}

export const MapButton = ({
  locale,
  queryParams,
  className,
  iconClassName,
  size = 'xs',
  rounding = 'normal',
  styling = 'soft',
  color = 'ink-medium',
}: MapButtonProps) => {
  const { t } = useTranslation('common');
  const headerSearch = useContext(HeaderSearchContext);

  if (!googleAPIKey) return null;

  const handleOpenMap = () => {
    void (async () => {
      const { showJobsMapModal } = await import('@components/templates/job/list/JobsMapModal');
      showJobsMapModal({
        googleApiKey: googleAPIKey,
        locale,
        queryParams: queryParams ?? headerSearch?.queryParams,
      });
    })();
  };

  return (
    <Button
      styling={styling}
      rounding={rounding}
      color={color}
      size={size}
      className={className}
      onClick={handleOpenMap}
      aria-label={t('add.job.open.map')}
      title={t('add.job.open.map')}
    >
      <MapIcon className={iconClassName ?? 'size-6'} />
    </Button>
  );
};
